import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DataGrid as DxDataGrid, IDataGridOptions } from 'devextreme-react/data-grid';
import { useNavigate } from 'react-router-dom';
import {
  Column,
  ColumnChooser,
  FilterRow,
  HeaderFilter,
  Item,
  NuvoDataGrid,
  Pager,
  Paging,
  Selection,
  Sorting,
  Toolbar,
  StateStoring,
  Lookup,
} from '@nuvolo/nuux-core/components/NuvoDataGrid';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import type { ClickEvent } from 'devextreme/ui/button';
import Styles from './Styles';
import type DataGridProps from './DataGridProps';
import ToolbarButton from './ToolbarButton';
import type { ActionFn, CellRenderEvent, ColumnProps, PredefinedAction } from './DataGridProps';
import parseUrl from '../../utils/parsers/parseUrl';
import { isType } from '../../utils/typeGuards';
import useDataStore from '#/common/hooks/useDataStore';
import { EventInfo } from 'devextreme/events';
import dxDataGrid, { SelectionChangedInfo } from 'devextreme/ui/data_grid';
import useSearchQuery from '#/common/hooks/useSearchQuery';
import { clearFilters } from '#/common/utils/clearFilters';
import { get } from 'lodash';
import useStateStoring, { GridState } from './useStateStoring';
import { getColumnHeaderFilter, createColumnFilterExpression } from './utils';
import { useApolloClient } from '@apollo/client';
import { NULL_LABEL, NULL_STR } from '#/config/consts';
import useSession from '#/common/hooks/useSession';
import { utcToLocalString, parseLocalDateString } from '#/common/utils/datetimeTransform';
import Anchor from '../Anchor';
import { NuvoLink } from '@nuvolo/nuux-core/components/NuvoLink';

const allowedPageSizes: number[] = [5, 10, 15, 25];
const pageSizeThreshold = 10;
const remoteOperations: IDataGridOptions['remoteOperations'] = {
  paging: true,
  filtering: true,
  sorting: true,
  grouping: true,
  summary: true,
  groupPaging: true,
};

const DataGrid = <T,>(props: DataGridProps<T>) => {
  const query = useSearchQuery();

  const {
    id,
    keyExpr = 'id' as keyof T & string,
    pluralText = 'Items',
    columns,
    children,
    recordUrl = ':id',
    selectedActions = [],
    actions = [],
    selectionMode = 'multiple',
    onRowClick,
    getItemsQuery,
    getGroupedItemList,
    dataSource,
    permanentFilters,
    setRef,
    disablePaging = false,
    disableSorting = false,
    disableFiltering = false,
    disableStateStoring = false,
    stateStoringColumnsOnly = false,
    allowColumnReordering = true,
    defaultPageSize = 10,
    toolbarPrefix,
    onGroupCountChange,
    onRowExpanding,
    onRowExpanded,
    onRowCollapsed,
    expandedParentIds,
    getExpandedParentIds,
    groupExpandFilterMode,
    groupExpandKeyField,
    onGridOptionChanged,
    onCellPrepared,
    extraQueryParams,
  } = props;
  const dg = useRef<DxDataGrid>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (dg.current && setRef) {
      setRef(dg.current!);
    }
  }, [dg.current]);

  // Do not show Tenant column if single tenant
  const { tenantIDs } = useSession();
  const filteredColumns = useMemo(
    () =>
      tenantIDs?.length === 1
        ? columns.filter(
            (col) => col.dataField !== 'tenantId' && !col.dataField.startsWith('tenant.'),
          )
        : columns,
    [columns, tenantIDs],
  );

  const gqlClient = useApolloClient();
  const dataStore = getItemsQuery
    ? useDataStore<T>({
        getItemList: getItemsQuery,
        getGroupedItemList: getGroupedItemList,
        columns: filteredColumns,
        filter: permanentFilters,
        onGroupCountChange,
        expandedParentIds,
        getExpandedParentIds,
        groupExpandFilterMode,
        groupExpandKeyField,
        extraQueryParams,
      })
    : dataSource || [];
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const hasSelection = selectedItems.length > 0;
  const selectionEnabled = selectedActions.length > 0;

  const showColumnChooser = useCallback(() => {
    dg.current?.instance.showColumnChooser();
  }, []);

  const resetSettings = useCallback(() => {
    dg.current?.instance.state(null);
    dg.current?.instance.clearFilter();
  }, []);

  const removeFilters = useCallback(() => {
    dg.current?.instance.clearFilter();
  }, []);

  const refreshData = useCallback(() => {
    dg.current?.instance.refresh().catch((e) => console.error(e));
  }, []);

  const cellRenderLink = useCallback(
    (c: CellRenderEvent<T>): ReactNode => {
      const editorOptions = c.column.editorOptions as ColumnProps<T>;

      // Don't render a link in group/summary rows — prevents nested <a> DOM violations.
      const rowType = (c as any)?.rowType;
      const summaryItem = (c as any)?.summaryItem;
      if (summaryItem || rowType === 'group' || rowType === 'groupFooter') {
        return <span>{c.displayValue}</span>;
      }

      const onClick = () => {
        if (editorOptions?.onClick) {
          editorOptions.onClick({
            row: c.data,
            navigate,
          });
        }
      };

      const path = editorOptions?.path
        ? editorOptions.path(c.data)
        : parseUrl(recordUrl, c.data as Record<string, unknown>);

      return (
        <Anchor path={path} onClick={onClick}>
          <NuvoLink type="body">{c.displayValue}</NuvoLink>
        </Anchor>
      );
    },
    [navigate, recordUrl],
  );

  const getActionClick = (action: ActionFn<T>) => {
    return (e: ClickEvent, buttonRef?: React.RefObject<HTMLDivElement | null>) => {
      if (!action.onClick) {
        return;
      }

      const result = action.onClick({
        dg: dg.current,
        navigate,
        selectedKeys: selectedItems.map((s: T) => s[keyExpr] as string),
        selectedItems: selectedItems,
        setIsLoading: (val: boolean, message?: string) => {
          if (val) {
            dg.current?.instance.beginCustomLoading(message || '');
            e.component.option('disabled', true);
          } else {
            dg.current?.instance.endCustomLoading();
            e.component.option('disabled', false);
          }
        },
        buttonRef,
      });
    };
  };

  const getActionDisabled = (action: ActionFn<T>, defaultValue: boolean) => {
    if (isType(action.disabled, 'boolean')) {
      return action.disabled;
    }

    if (isType(action.disabled, 'function')) {
      return action.disabled({
        dg: dg.current,
        navigate,
        selectedKeys: selectedItems.map((s: T) => s[keyExpr] as string),
        selectedItems: selectedItems as T[],
      });
    }

    if (!action.onClick) {
      return true;
    }

    return defaultValue;
  };

  const predefinedActions: Record<PredefinedAction, ReactNode> = {
    'column-chooser': (
      <Item key="column-chooser" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Select Columns" icon="table-columns" onClick={showColumnChooser} />
      </Item>
    ),
    'reset-settings': (
      <Item key="reset-settings" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Reset Settings" icon="reset_settings" onClick={resetSettings} />
      </Item>
    ),
    'clear-filters': (
      <Item key="clear-filters" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Clear Filters" icon="filter-slash" onClick={removeFilters} />
      </Item>
    ),
    'refresh-data': (
      <Item key="refresh-data" locateInMenu="auto" location="after">
        <ToolbarButton tooltip="Reload data" icon="arrow-rotate-right" onClick={refreshData} />
      </Item>
    ),
  };

  const setSelectedData = useCallback(
    (e: EventInfo<dxDataGrid<unknown, unknown>> & SelectionChangedInfo<unknown, unknown>) => {
      const selectedRowsData = (e.selectedRowsData as T[]) ?? [];

      const transformedRows = selectedRowsData.map((row) => {
        const transformed = { ...row } as Record<string, unknown>;
        filteredColumns
          .map((col) => col.dataField)
          .forEach((fieldName) => {
            if (fieldName in transformed && transformed[fieldName] === NULL_STR) {
              transformed[fieldName] = null;
            }
          });

        return transformed as T;
      });

      setSelectedItems(transformedRows);
    },
    [],
  );

  const initialState: GridState = {};
  if (query.filters.length > 0) {
    const column: any[] = filteredColumns.map((c) => {
      return { dataField: c.dataField, dataType: c.dataType };
    });
    const { filtersClean } = clearFilters(query.filters, column);
    if (filtersClean.length > 0) {
      initialState.filterValue = filtersClean;
    }
  }

  const stateStoring = useStateStoring(
    id,
    filteredColumns,
    initialState,
    stateStoringColumnsOnly ? 'columnsOnly' : 'default',
  );

  const handleOptionChanged = useCallback(
    (e: any) => {
      onGridOptionChanged?.(e);
      if (!disableStateStoring && e?.name === 'filterValue' && e?.fullName === 'filterValue') {
        stateStoring.updateFilters(e.value);
      }
    },
    [disableStateStoring, onGridOptionChanged, stateStoring.updateFilters],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (dg.current?.instance) {
        const count = dg.current.instance.totalCount();
        const currentPageSize = dg.current.instance.pageSize();
        if (count !== totalCount) {
          setTotalCount(count);
        }
        if (currentPageSize !== pageSize) {
          setPageSize(currentPageSize);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [totalCount, pageSize]);

  return (
    <Styles>
      <NuvoDataGrid
        ref={dg}
        dataSource={dataStore}
        allowColumnReordering={allowColumnReordering}
        allowColumnResizing
        showBorders={false}
        onSelectionChanged={setSelectedData}
        onRowClick={onRowClick as any}
        onOptionChanged={handleOptionChanged}
        onRowExpanding={onRowExpanding}
        onRowExpanded={onRowExpanded}
        onRowCollapsed={onRowCollapsed}
        onCellPrepared={onCellPrepared}
        columnResizingMode="nextColumn"
        columnMinWidth={50}
        remoteOperations={remoteOperations}
        filterSyncEnabled>
        {filteredColumns.map((c) => {
          if (
            isType(c.lookup?.dataSource, 'array') &&
            c.lookup?.nullable &&
            !c.lookup.dataSource.find(
              (item: any) => item[c.lookup?.valueExpr ?? 'value'] === NULL_STR,
            )
          ) {
            c.lookup.dataSource = [
              ...c.lookup.dataSource,
              {
                [c.lookup.valueExpr ?? 'value']: NULL_STR,
                [c.lookup.displayExpr ?? 'text']: NULL_LABEL,
              },
            ];
          }
          return (
            <Column
              key={c.dataField}
              dataField={c.dataField}
              caption={c.caption}
              showInColumnChooser={c.showInColumnChooser}
              dataType={c.dataType ?? 'string'}
              visible={!!c.visible}
              fixed={c.fixed}
              groupCellRender={c.groupCellRender}
              sortIndex={get(c, 'sortIndex')}
              sortOrder={get(c, 'sortOrder')}
              width={c.width || (c.fixed && c.dataField === 'displayNumber' ? 130 : undefined)}
              allowHiding={!c.fixed}
              allowSearch={c.allowSearch ?? true}
              allowFiltering={c.allowFiltering ?? !c.lookup}
              allowSorting={c.allowSorting ?? true}
              filterType={c.filterType}
              filterValues={c.filterValues}
              groupIndex={c.groupIndex ?? undefined}
              calculateSortValue={c.calcSortValue || c.dataField}
              calculateFilterExpression={
                c.calcFilterExpression
                  ? (value: unknown, operation: string) => c.calcFilterExpression!(value, operation)
                  : createColumnFilterExpression(c.dataField, c.dataType, c.filterField)
              }
              calculateGroupValue={c.dataField}
              calculateDisplayValue={
                c.calcDisplayValue || c.lookup
                  ? c.calcDisplayValue
                  : (row: T) => {
                      const value = get(row, c.dataField);
                      if (value === NULL_STR) {
                        return '';
                      }
                      if (c.dataType === 'datetime') {
                        return utcToLocalString(value as string);
                      }
                      if (c.dataType === 'date') {
                        if (value instanceof Date) {
                          // DevExtreme parsed "YYYY-MM-DD" as UTC midnight; recover the
                          // intended calendar date from the UTC components so that local
                          // rendering shows the correct day regardless of UTC offset.
                          return new Date(
                            value.getUTCFullYear(),
                            value.getUTCMonth(),
                            value.getUTCDate(),
                          );
                        }
                        if (typeof value === 'string') {
                          return parseLocalDateString(value);
                        }
                      }
                      return value;
                    }
              }
              editorOptions={c}
              cellRender={c.openOnClick ? cellRenderLink : c.cellRender}
              format={c.format}
              alignment={c.alignment}
              allowHeaderFiltering={!!c.headerFilter || !!c.lookup}>
              {c.lookup && <Lookup {...c.lookup} />}
              {(c.headerFilter || c.lookup) && (
                <HeaderFilter allowSearch {...getColumnHeaderFilter(gqlClient, c)} visible={true} />
              )}
            </Column>
          );
        })}

        <Selection mode={selectionMode} allowSelectAll={false} />

        {!disablePaging && <Paging enabled defaultPageSize={defaultPageSize} />}
        {!disablePaging && (
          <Pager
            visible={totalCount > pageSizeThreshold || getGroupedItemList !== undefined}
            allowedPageSizes={allowedPageSizes}
            displayMode="full"
            showInfo
            showNavigationButtons
            showPageSizeSelector={
              totalCount > pageSizeThreshold || getGroupedItemList !== undefined
            }
          />
        )}

        {!disableSorting && <Sorting mode="single" />}

        {!disableFiltering && <HeaderFilter visible />}
        {!disableFiltering && <FilterRow visible />}

        <Toolbar visible>
          <Item locateInMenu="auto" location="before" visible={selectionEnabled}>
            <NuvoBox>
              Selected {pluralText}: {selectedItems.length}
            </NuvoBox>
          </Item>

          <Item name="groupPanel" location="center" locateInMenu="auto" />

          {toolbarPrefix && (
            <Item key="toolbar-prefix" location="after">
              {toolbarPrefix}
            </Item>
          )}

          {actions.map((a) =>
            typeof a === 'string' ? (
              predefinedActions[a]
            ) : (
              <Item key={`action-${a.key}`} locateInMenu="auto" location="after">
                <ToolbarButton
                  tooltip={a.text}
                  icon={a.icon}
                  showTextOnly={a.showTextOnly}
                  type={a.type}
                  onClick={getActionClick(a)}
                  disabled={getActionDisabled(a, false)}
                />
              </Item>
            ),
          )}

          {selectedActions.map((a) => (
            <Item key={a.key} locateInMenu="auto" location="after" visible={totalCount > 0}>
              <ToolbarButton
                tooltip={a.text}
                icon={a.icon}
                type={a.type}
                onClick={getActionClick(a)}
                disabled={getActionDisabled(a, !hasSelection)}
              />
            </Item>
          ))}

          <Item name="searchPanel" location="after" locateInMenu="auto" />
        </Toolbar>

        <ColumnChooser enabled />
        <StateStoring
          enabled={!disableStateStoring}
          type="custom"
          customLoad={stateStoring.load}
          customSave={stateStoring.save}
          savingTimeout={100}
        />

        {children}
      </NuvoDataGrid>
    </Styles>
  );
};

export default DataGrid;
